import React, { useState, useMemo, useEffect } from "react";
import {
  Table, Row, Col, Form, Button,
  Pagination, Dropdown, Badge, Alert, Spinner
} from "react-bootstrap";
import Footer from "../layouts/Footer";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// STYLES PREMIUM
// ============================================
const AM_STYLES = `
  .am-page { background: #f8fafc; min-height: 100vh; }
  .am-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

  .am-header {
    background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
    border-radius: 16px; padding: 24px 28px;
    border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  }
  .am-header-icon {
    width: 56px; height: 56px; border-radius: 14px;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 26px; box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
  }
  .am-header-title { font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em; }
  .am-header-subtitle { color: #64748b; font-size: 0.875rem; }

  .am-btn-primary, .am-btn-secondary, .am-btn-outline-success, .am-btn-outline-secondary, .am-btn-outline-danger {
    display: inline-flex; align-items: center; justify-content: center;
    gap: 8px; padding: 11px 20px; border-radius: 10px;
    font-weight: 600; font-size: 0.875rem; border: none;
    transition: all 0.2s; cursor: pointer; text-decoration: none;
  }
  .am-btn-primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
  }
  .am-btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
    box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
  }
  .am-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .am-btn-large { padding: 12px 24px; font-size: 0.9rem; }
  .am-btn-secondary {
    background: white; border: 1px solid #e2e8f0; color: #334155;
  }
  .am-btn-secondary:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
  .am-btn-outline-success {
    background: #d1fae5; color: #059669; border: 1px solid #a7f3d0;
  }
  .am-btn-outline-success:hover { background: #10b981; color: white; }
  .am-btn-outline-secondary {
    background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0;
  }
  .am-btn-outline-secondary:hover { background: #64748b; color: white; }
  .am-btn-outline-danger {
    background: #fee2e2; color: #dc2626; border: 1px solid #fecaca;
  }
  .am-btn-outline-danger:hover { background: #ef4444; color: white; }

  .am-alert {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 18px; border-radius: 12px;
    font-size: 0.85rem; font-weight: 500;
  }
  .am-alert > i { font-size: 1.3rem; flex-shrink: 0; }
  .am-alert-success {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    color: #065f46; border-left: 4px solid #10b981;
  }
  .am-alert-success > i { color: #10b981; }
  .am-alert-danger {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    color: #991b1b; border-left: 4px solid #ef4444;
  }
  .am-alert-danger > i { color: #ef4444; }
  .am-alert-warning {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    color: #92400e; border-left: 4px solid #f59e0b;
  }
  .am-alert-warning > i { color: #f59e0b; }

  .am-config-card {
    background: white; border-radius: 16px;
    border: 1px solid #e2e8f0; overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  }
  .am-config-header {
    padding: 20px 24px;
    background: linear-gradient(180deg, #ffffff, #fafbfc);
    border-bottom: 1px solid #f1f5f9;
  }
  .am-config-icon {
    width: 42px; height: 42px; border-radius: 10px;
    background: #eff6ff; color: #3b82f6;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }
  .am-config-title { font-weight: 700; color: #0f172a; font-size: 0.95rem; margin: 0; }
  .am-config-subtitle { color: #94a3b8; font-size: 0.75rem; }
  .am-config-body { padding: 28px; }
  .am-config-grid {
    display: grid; grid-template-columns: 1fr 1fr 220px;
    gap: 20px; align-items: end;
  }

  .am-form-group { margin-bottom: 4px; }
  .am-form-label {
    display: block; font-weight: 700; color: #0f172a;
    font-size: 0.82rem; margin-bottom: 8px;
  }
  .am-required { color: #ef4444; }
  .am-input-wrapper { position: relative; display: flex; align-items: center; }
  .am-input-icon {
    position: absolute; left: 16px; color: #94a3b8;
    font-size: 1.05rem; pointer-events: none; z-index: 5;
  }
  .am-input, .am-select {
    width: 100%; padding: 12px 16px 12px 46px;
    border: 2px solid #e2e8f0; border-radius: 10px;
    font-size: 0.875rem; color: #0f172a;
    background: white; transition: all 0.2s;
    outline: none; font-family: inherit;
  }
  .am-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2394a3b8'%3E%3Cpath d='M12 16L6 10H18L12 16Z'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 20px;
    cursor: pointer;
    padding-right: 40px;
  }
  .am-input:focus, .am-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
  .am-form-hint {
    margin-top: 6px; font-size: 0.72rem;
    color: #94a3b8; display: flex; align-items: center; gap: 4px;
  }
  .am-form-hint i { color: #3b82f6; }

  .am-info-box {
    display: flex; align-items: flex-start; gap: 14px;
    padding: 16px 18px; border-radius: 12px;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #1e40af; border-left: 4px solid #3b82f6;
  }
  .am-info-box-icon {
    width: 40px; height: 40px; border-radius: 10px;
    background: #3b82f6; color: white;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
  }
  .am-info-box-content strong { display: block; font-size: 0.85rem; margin-bottom: 4px; }
  .am-info-box-content p { margin: 0; font-size: 0.82rem; opacity: 0.85; line-height: 1.5; }

  .am-table-card {
    background: white; border-radius: 16px;
    border: 1px solid #e2e8f0; overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  }
  .am-table-header {
    padding: 20px 24px;
    background: linear-gradient(180deg, #ffffff, #fafbfc);
    border-bottom: 1px solid #f1f5f9;
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; gap: 12px;
  }
  .am-table-header-success {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-bottom: 1px solid #bbf7d0;
  }
  .am-table-header-icon {
    width: 42px; height: 42px; border-radius: 10px;
    background: #eff6ff; color: #3b82f6;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }
  .am-table-header-success .am-table-header-icon { background: #10b981; color: white; }
  .am-table-title { font-weight: 700; color: #0f172a; font-size: 0.95rem; margin: 0; }
  .am-table-subtitle { color: #94a3b8; font-size: 0.75rem; }
  .am-table-body { padding: 0; }

  .am-filters {
    padding: 16px 24px; background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  .am-search-wrapper { position: relative; display: flex; align-items: center; }
  .am-search-icon {
    position: absolute; left: 14px; color: #94a3b8;
    font-size: 1.05rem; pointer-events: none;
  }
  .am-search-input {
    width: 100%; padding: 11px 40px 11px 42px;
    border: 1px solid #e2e8f0; border-radius: 10px;
    font-size: 0.875rem; background: white;
    transition: all 0.2s; outline: none; font-family: inherit;
  }
  .am-search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  .am-search-clear {
    position: absolute; right: 8px; background: none; border: none;
    color: #94a3b8; font-size: 1rem; padding: 6px 10px;
    cursor: pointer; border-radius: 6px; transition: all 0.15s;
  }
  .am-search-clear:hover { background: #fee2e2; color: #ef4444; }
  .am-select-full {
    width: 100%; padding: 11px 14px;
    border: 1px solid #e2e8f0; border-radius: 10px;
    font-size: 0.875rem; font-weight: 500;
    background: white; color: #334155;
    outline: none; cursor: pointer; font-family: inherit;
  }
  .am-select-full:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .am-table { font-size: 0.875rem; }
  .am-table thead th {
    background: #f8fafc; color: #475569;
    font-weight: 700; font-size: 0.7rem;
    text-transform: uppercase; letter-spacing: 0.05em;
    padding: 16px; border-bottom: 1px solid #e2e8f0;
    border-top: none; white-space: nowrap;
  }
  .am-table tbody td {
    padding: 16px; vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
  }
  .am-row { transition: background-color 0.15s; }
  .am-row:hover { background-color: #f8fafc; }
  .am-sortable-header { cursor: pointer; user-select: none; }
  .am-sortable-header:hover { color: #3b82f6; }
  .am-sortable-content { display: inline-flex; align-items: center; gap: 6px; }
  .am-sort-icon { color: #3b82f6; font-size: 0.75rem; }

  .am-checkbox-wrapper { display: flex; align-items: center; justify-content: center; }
  .am-checkbox {
    width: 18px; height: 18px; border-radius: 5px;
    border: 1.5px solid #cbd5e1; cursor: pointer;
    accent-color: #3b82f6;
  }

  .am-title-cell { max-width: 280px; }
  .am-title-main {
    font-weight: 700; color: #0f172a; font-size: 0.85rem;
    margin-bottom: 4px; line-height: 1.3;
  }
  .am-title-desc {
    font-size: 0.72rem; color: #94a3b8;
    overflow: hidden; text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .am-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 11px; border-radius: 6px;
    font-size: 0.72rem; font-weight: 700;
    white-space: nowrap;
  }
  .am-badge-blue   { background: #eff6ff; color: #3b82f6; }
  .am-badge-green  { background: #d1fae5; color: #059669; }
  .am-badge-amber  { background: #fef3c7; color: #d97706; }
  .am-badge-info   { background: #cffafe; color: #0891b2; }
  .am-badge-violet { background: #ede9fe; color: #7c3aed; }
  .am-badge-gray   { background: #f1f5f9; color: #64748b; }

  .am-code {
    font-size: 0.72rem; color: #1e40af;
    background: #eff6ff; padding: 4px 8px;
    border-radius: 5px; font-weight: 600;
    font-family: 'SF Mono', Menlo, Monaco, monospace;
  }
  .am-link { color: #3b82f6; text-decoration: none; font-size: 0.8rem; }
  .am-link:hover { text-decoration: underline; }

  .am-actions { display: inline-flex; gap: 6px; justify-content: flex-end; }
  .am-action-btn {
    width: 34px; height: 34px; border-radius: 8px;
    border: 1px solid #e2e8f0; background: white;
    color: #64748b; display: inline-flex;
    align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s; font-size: 0.9rem;
  }
  .am-action-view:hover {
    background: #06b6d4; border-color: #06b6d4; color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
  }
  .am-action-edit:hover {
    background: #3b82f6; border-color: #3b82f6; color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
  }
  .am-action-delete:hover {
    background: #ef4444; border-color: #ef4444; color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
  }

  .am-loading {
    display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    min-height: 60vh;
  }
  .am-loading-spinner {
    width: 56px; height: 56px;
    border: 5px solid #e2e8f0; border-top-color: #3b82f6;
    border-radius: 50%; animation: am-spin 0.8s linear infinite;
    margin-bottom: 20px;
  }
  .am-loading-text { color: #64748b; font-size: 0.9rem; font-weight: 500; }
  .am-loading-inline {
    padding: 40px; text-align: center;
    display: flex; flex-direction: column;
    align-items: center; gap: 12px;
    color: #64748b; font-size: 0.85rem;
  }
  .am-loading-spinner-small {
    width: 32px; height: 32px;
    border: 3px solid #e2e8f0; border-top-color: #3b82f6;
    border-radius: 50%; animation: am-spin 0.8s linear infinite;
  }
  .am-empty-state { padding: 60px 20px; text-align: center; }
  .am-empty-icon {
    width: 80px; height: 80px; border-radius: 50%;
    background: #f1f5f9; color: #94a3b8;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 36px; margin-bottom: 20px;
  }
  .am-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; }
  .am-empty-text { color: #94a3b8; margin-bottom: 20px; }
  .am-btn-empty-reset {
    display: inline-flex; align-items: center;
    background: white; color: #3b82f6;
    border: 1px solid #bfdbfe; border-radius: 8px;
    font-weight: 600; padding: 8px 16px;
    cursor: pointer; transition: all 0.2s;
  }
  .am-btn-empty-reset:hover { background: #3b82f6; color: white; border-color: #3b82f6; }
  .am-empty-inline {
    display: flex; align-items: center; gap: 14px;
    padding: 20px; border-radius: 12px;
    background: #fef3c7; color: #92400e;
    border-left: 4px solid #f59e0b;
  }
  .am-empty-inline > i { font-size: 1.5rem; color: #f59e0b; flex-shrink: 0; }
  .am-empty-inline strong { display: block; font-size: 0.9rem; margin-bottom: 4px; }
  .am-empty-inline p { margin: 0; font-size: 0.82rem; opacity: 0.85; }

  .am-stat-card {
    display: flex; align-items: center; gap: 14px;
    background: white; border-radius: 14px;
    padding: 18px 20px; border: 1px solid #e2e8f0;
    transition: all 0.25s;
  }
  .am-stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
  }
  .am-stat-icon-wrapper {
    width: 46px; height: 46px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; flex-shrink: 0;
  }
  .am-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
  .am-stat-icon-green  { background: #d1fae5; color: #10b981; }
  .am-stat-icon-amber  { background: #fef3c7; color: #f59e0b; }
  .am-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }
  .am-stat-content { min-width: 0; flex-grow: 1; }
  .am-stat-label {
    font-size: 0.72rem; color: #64748b; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
  }
  .am-stat-value {
    font-size: 1.5rem; font-weight: 700; color: #0f172a;
    letter-spacing: -0.02em; line-height: 1;
  }

  .am-pagination {
    display: flex; justify-content: space-between; align-items: center;
    padding: 16px 24px; background: #f8fafc;
    border-top: 1px solid #e2e8f0; flex-wrap: wrap; gap: 12px;
  }
  .am-pagination-info {
    font-size: 0.8rem; color: #64748b;
    display: flex; align-items: center; gap: 6px;
  }
  .am-pagination-info strong { color: #0f172a; }
  .am-pagination-controls { display: flex; align-items: center; gap: 6px; }
  .am-page-btn {
    min-width: 34px; height: 34px; padding: 0 10px;
    border-radius: 8px; border: 1px solid #e2e8f0;
    background: white; color: #475569;
    display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
    font-size: 0.8rem; font-weight: 600;
  }
  .am-page-btn:hover:not(:disabled) {
    background: #3b82f6; color: white; border-color: #3b82f6;
  }
  .am-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .am-page-btn.active {
    background: #3b82f6; color: white; border-color: #3b82f6;
  }

  .am-spin { animation: am-spin 1s linear infinite; display: inline-block; }
  @keyframes am-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (max-width: 992px) {
    .am-config-grid { grid-template-columns: 1fr 1fr; }
    .am-form-group-action { grid-column: 1 / -1; }
  }
  @media (max-width: 768px) {
    .am-header { padding: 20px; }
    .am-header-title { font-size: 1.2rem; }
    .am-config-body { padding: 20px; }
    .am-config-grid { grid-template-columns: 1fr; }
    .am-pagination { flex-direction: column; }
    .am-stat-value { font-size: 1.25rem; }
  }
`;

const initialAlertes = [];

export default function AlertesManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('dateCreation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedAlertes, setSelectedAlertes] = useState([]);
  const [alertes, setAlertes] = useState(initialAlertes);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
  const [selectedSeveriteFilter, setSelectedSeveriteFilter] = useState('all');
  const [selectedStatutFilter, setSelectedStatutFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState({ type: '', text: '' });

  const { user, business } = useAuth();

  const [formSeuil, setFormSeuil] = useState({
    seuil: '',
    type_notification: 'email'
  });

  const [seuilActuel, setSeuilActuel] = useState(null);
  const [loadingSeuil, setLoadingSeuil] = useState(false);

  // ============================================
  // ✅ FONCTION ROBUSTE : Récupérer business_id
  // ============================================
  const getBusinessId = () => {
    // 1. Depuis le contexte business
    if (business?.business_id) return business.business_id;

    // 2. Depuis le user
    if (user?.business_id) return user.business_id;

    // 3. Depuis localStorage (businessData)
    try {
      const stored = JSON.parse(localStorage.getItem('businessData') || 'null');
      if (stored?.business_id) return stored.business_id;
    } catch (e) {
      console.error('Erreur parsing businessData:', e);
    }

    // 4. Depuis localStorage (userData)
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || 'null');
      if (userData?.business_id) return userData.business_id;
    } catch (e) {
      console.error('Erreur parsing userData:', e);
    }

    // 5. Décoder le token JWT
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload?.business_id) return payload.business_id;
      }
    } catch (e) {
      console.error('Erreur décodage token:', e);
    }

    return null;
  };

  // ============================================
  // FETCH SEUIL
  // ============================================
  const fetchSeuilActuel = async () => {
    const businessId = getBusinessId();
    if (!businessId) {
      console.log('⚠️ business_id non disponible, attente...');
      return;
    }

    setLoadingSeuil(true);
    try {
      const response = await apiClient.get(`/seuil/${businessId}`);
      if (response.data.code === 200) {
        setSeuilActuel(response.data.data);
      } else {
        setSeuilActuel(null);
      }
    } catch (error) {
      console.error('Erreur seuil:', error);
      setSeuilActuel(null);
    } finally {
      setLoadingSeuil(false);
    }
  };

  useEffect(() => {
    fetchSeuilActuel();
  }, [business, user]);

  // ============================================
  // SAVE SEUIL
  // ============================================
  const handleSaveSeuil = async () => {
    if (!formSeuil.seuil || formSeuil.seuil <= 0) {
      setApiMessage({ type: 'error', text: 'Veuillez saisir un seuil valide' });
      return;
    }

    setLoading(true);
    setApiMessage({ type: '', text: '' });

    try {
      const response = await apiClient.post('/seuil', {
        seuil: parseInt(formSeuil.seuil),
        type_notification: formSeuil.type_notification
      });

      setApiMessage({
        type: 'success',
        text: response.data.message || 'Seuil d\'alerte enregistré avec succès'
      });

      await fetchSeuilActuel();

      const nouvelleAlerte = {
        id: Math.max(...alertes.map(a => a.id), 0) + 1,
        titre: `Alerte seuil - ${formSeuil.seuil} SMS`,
        type: "Solde",
        severite: formSeuil.seuil > 1000 ? "Haute" : "Moyenne",
        statut: "Active",
        dateCreation: new Date().toLocaleString('fr-FR'),
        dateDerniereDeclenchement: new Date().toLocaleString('fr-FR'),
        declenchements: 0,
        condition: `Nombre de SMS >= ${formSeuil.seuil}`,
        destinataires: ["admin@system.com"],
        description: `Alerte automatique lorsque le nombre de SMS atteint ${formSeuil.seuil}`,
        canal: formSeuil.type_notification === 'email' ? 'Email' :
               formSeuil.type_notification === 'sms' ? 'SMS' : 'Email et SMS',
        frequence: "Immédiate"
      };

      setAlertes(prev => [nouvelleAlerte, ...prev]);
      setFormSeuil({ seuil: '', type_notification: 'email' });
    } catch (error) {
      console.error('Erreur sauvegarde seuil:', error);
      setApiMessage({
        type: 'error',
        text: `Erreur: ${error.response?.data?.message || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = alertes.filter(alerte => {
      const matchesSearch =
        alerte.titre.toLowerCase().includes(filterText.toLowerCase()) ||
        alerte.description.toLowerCase().includes(filterText.toLowerCase()) ||
        alerte.condition.toLowerCase().includes(filterText.toLowerCase()) ||
        alerte.destinataires.some(dest => dest.toLowerCase().includes(filterText.toLowerCase()));

      const matchesType = selectedTypeFilter === 'all' || alerte.type === selectedTypeFilter;
      const matchesSeverite = selectedSeveriteFilter === 'all' || alerte.severite === selectedSeveriteFilter;
      const matchesStatut = selectedStatutFilter === 'all' || alerte.statut === selectedStatutFilter;

      return matchesSearch && matchesType && matchesSeverite && matchesStatut;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [filterText, sortField, sortDirection, selectedTypeFilter, selectedSeveriteFilter, selectedStatutFilter, alertes]);

  // ============================================
  // STATS
  // ============================================
  const calculatedStats = useMemo(() => {
    const total = alertes.length;
    const actives = alertes.filter(a => a.statut === "Active").length;
    const enPause = alertes.filter(a => a.statut === "En pause").length;
    const totalDeclenchements = alertes.reduce((sum, a) => sum + a.declenchements, 0);

    return { total, actives, enPause, totalDeclenchements };
  }, [alertes]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedAlertes(currentItems.map(a => a.id));
    else setSelectedAlertes([]);
  };

  const handleSelectAlerte = (alerteId) => {
    setSelectedAlertes(prev =>
      prev.includes(alerteId)
        ? prev.filter(id => id !== alerteId)
        : [...prev, alerteId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedAlertes.length === currentItems.length;
  const isSomeSelected = selectedAlertes.length > 0 && selectedAlertes.length < currentItems.length;

  // ============================================
  // HANDLERS
  // ============================================
  const handleBulkToggleStatus = (newStatus) => {
    if (selectedAlertes.length === 0) {
      alert("Veuillez sélectionner au moins une alerte");
      return;
    }
    setAlertes(prev => prev.map(a => selectedAlertes.includes(a.id) ? { ...a, statut: newStatus } : a));
    setSelectedAlertes([]);
  };

  const handleDeleteAlerte = (alerteId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette alerte ?")) {
      setAlertes(prev => prev.filter(a => a.id !== alerteId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedAlertes.length === 0) return;
    if (window.confirm(`Supprimer ${selectedAlertes.length} alerte(s) ?`)) {
      setAlertes(prev => prev.filter(a => !selectedAlertes.includes(a.id)));
      setSelectedAlertes([]);
    }
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="am-sortable-header" onClick={() => handleSort(field)}>
      <span className="am-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill am-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  const getSeveriteConfig = (severite) => {
    const config = {
      'Critique': { color: '#ef4444', bg: '#fee2e2', icon: 'bi-exclamation-octagon-fill' },
      'Haute': { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-exclamation-triangle-fill' },
      'Moyenne': { color: '#3b82f6', bg: '#dbeafe', icon: 'bi-info-circle-fill' },
      'Basse': { color: '#10b981', bg: '#d1fae5', icon: 'bi-check-circle-fill' }
    };
    return config[severite] || { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-question-circle' };
  };

  const getStatutConfig = (statut) => {
    const config = {
      'Active': { color: '#10b981', bg: '#d1fae5', icon: 'bi-play-circle-fill' },
      'Inactive': { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-pause-circle-fill' },
      'En pause': { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-pause-fill' },
      'En test': { color: '#3b82f6', bg: '#dbeafe', icon: 'bi-gear-fill' }
    };
    return config[statut] || { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-question-circle' };
  };

  const getTypeConfig = (type) => {
    const config = {
      'Solde': { color: '#10b981', bg: '#d1fae5', icon: 'bi-wallet-fill' },
      'Technique': { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-gear-fill' },
      'Campagne': { color: '#8b5cf6', bg: '#ede9fe', icon: 'bi-megaphone-fill' },
      'Sécurité': { color: '#ef4444', bg: '#fee2e2', icon: 'bi-shield-fill' },
      'Validation': { color: '#3b82f6', bg: '#dbeafe', icon: 'bi-check-circle-fill' }
    };
    return config[type] || { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-bell-fill' };
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="am-page main main-app p-3 p-lg-4">
      <style>{AM_STYLES}</style>

      {/* ========== HEADER ========== */}
      <div className="am-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="am-header-icon">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div>
              <h3 className="am-header-title mb-1">Gestion des alertes</h3>
              <p className="am-header-subtitle mb-0">
                Configurez les alertes de seuil et gérez les notifications
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedAlertes.length > 0 && (
              <>
                <button className="am-btn-outline-success" onClick={() => handleBulkToggleStatus('Active')}>
                  <i className="bi bi-play-circle"></i>
                  <span>Activer ({selectedAlertes.length})</span>
                </button>
                <button className="am-btn-outline-secondary" onClick={() => handleBulkToggleStatus('Inactive')}>
                  <i className="bi bi-pause-circle"></i>
                  <span>Désactiver ({selectedAlertes.length})</span>
                </button>
                <button className="am-btn-outline-danger" onClick={handleBulkDelete}>
                  <i className="bi bi-trash"></i>
                  <span>Supprimer ({selectedAlertes.length})</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ========== CONFIGURATION SEUIL ========== */}
      <div className="am-config-card mb-4">
        <div className="am-config-header">
          <div className="d-flex align-items-center gap-2">
            <div className="am-config-icon">
              <i className="bi bi-sliders"></i>
            </div>
            <div>
              <h6 className="am-config-title mb-0">Configuration du seuil d'alerte</h6>
              <small className="am-config-subtitle">Définissez le déclencheur automatique</small>
            </div>
          </div>
        </div>

        <div className="am-config-body">
          {apiMessage.text && (
            <div className={`am-alert am-alert-${apiMessage.type === 'success' ? 'success' : 'danger'} mb-4`}>
              <i className={`bi ${apiMessage.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`}></i>
              <div>{apiMessage.text}</div>
            </div>
          )}

          <div className="am-config-grid">
            <div className="am-form-group">
              <label className="am-form-label">
                Seuil d'alerte (nombre de SMS) <span className="am-required">*</span>
              </label>
              <div className="am-input-wrapper">
                <i className="bi bi-send am-input-icon"></i>
                <input
                  type="number"
                  className="am-input"
                  placeholder="Ex: 1000"
                  value={formSeuil.seuil}
                  onChange={(e) => setFormSeuil({ ...formSeuil, seuil: e.target.value })}
                  min="1"
                />
              </div>
              <div className="am-form-hint">
                <i className="bi bi-info-circle"></i>
                Nombre de SMS déclenchant l'alerte
              </div>
            </div>

            <div className="am-form-group">
              <label className="am-form-label">
                Type de notification <span className="am-required">*</span>
              </label>
              <div className="am-input-wrapper">
                <i className="bi bi-bell am-input-icon"></i>
                <select
                  className="am-select"
                  value={formSeuil.type_notification}
                  onChange={(e) => setFormSeuil({ ...formSeuil, type_notification: e.target.value })}
                >
                  <option value="email">Email uniquement</option>
                  <option value="sms">SMS uniquement</option>
                  <option value="both">Email et SMS</option>
                </select>
              </div>
            </div>

            <div className="am-form-group am-form-group-action">
              <button
                className="am-btn-primary am-btn-large"
                onClick={handleSaveSeuil}
                disabled={loading || !formSeuil.seuil}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <i className="bi bi-save"></i>
                    Enregistrer le seuil
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="am-info-box mt-4">
            <div className="am-info-box-icon">
              <i className="bi bi-info-circle-fill"></i>
            </div>
            <div className="am-info-box-content">
              <strong>Fonctionnement</strong>
              <p>
                Cette alerte se déclenchera automatiquement lorsque le nombre total de SMS atteindra le seuil défini.
                Vous recevrez une notification via le canal sélectionné.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ========== SEUIL ACTUEL ========== */}
      <div className="am-table-card mb-4">
        <div className="am-table-header am-table-header-success">
          <div className="d-flex align-items-center gap-2">
            <div className="am-table-header-icon">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div>
              <h6 className="am-table-title mb-0">Seuil d'alerte actuel</h6>
              <small className="am-table-subtitle">Configuration en production</small>
            </div>
          </div>
        </div>

        <div className="am-table-body">
          {loadingSeuil ? (
            <div className="am-loading-inline">
              <div className="am-loading-spinner-small"></div>
              <span>Chargement du seuil...</span>
            </div>
          ) : seuilActuel ? (
            <div className="table-responsive">
              <Table className="am-table mb-0">
                <thead>
                  <tr>
                    <th><i className="bi bi-hash me-1"></i>ID</th>
                    <th><i className="bi bi-building me-1"></i>Business</th>
                    <th><i className="bi bi-envelope me-1"></i>Email</th>
                    <th><i className="bi bi-phone me-1"></i>Téléphone</th>
                    <th><i className="bi bi-flag me-1"></i>Pays</th>
                    <th><i className="bi bi-send me-1"></i>Seuil SMS</th>
                    <th><i className="bi bi-coin me-1"></i>Seuil Crédits</th>
                    <th><i className="bi bi-bell me-1"></i>Notification</th>
                    <th><i className="bi bi-toggle-on me-1"></i>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="am-row">
                    <td><code className="am-code">#{seuilActuel.id}</code></td>
                    <td><strong>{seuilActuel.client?.business_name || seuilActuel.business_name || 'N/A'}</strong></td>
                    <td>
                      <a href={`mailto:${seuilActuel.client?.business_email || seuilActuel.business_email}`} className="am-link">
                        {seuilActuel.client?.business_email || seuilActuel.business_email || 'N/A'}
                      </a>
                    </td>
                    <td>
                      <a href={`tel:${seuilActuel.client?.phone || seuilActuel.phone}`} className="am-link">
                        {seuilActuel.client?.phone || seuilActuel.phone || 'N/A'}
                      </a>
                    </td>
                    <td>
                      <span className="am-badge am-badge-info">
                        <i className="bi bi-flag-fill"></i>
                        {seuilActuel.client?.country_code || seuilActuel.country_code || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className="am-badge am-badge-blue">
                        <i className="bi bi-send-fill"></i>
                        {seuilActuel.seuil || 0}
                      </span>
                    </td>
                    <td>
                      <span className="am-badge am-badge-amber">
                        <i className="bi bi-coin"></i>
                        {seuilActuel.seuil_credits || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`am-badge ${seuilActuel.type_notification === 'both' ? 'am-badge-green' : seuilActuel.type_notification === 'email' ? 'am-badge-blue' : 'am-badge-info'}`}>
                        <i className="bi bi-bell-fill"></i>
                        {seuilActuel.type_notification_formatted || seuilActuel.type_notification || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className={`am-badge ${seuilActuel.actif ? 'am-badge-green' : 'am-badge-gray'}`}>
                        <i className={`bi ${seuilActuel.actif ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                        {seuilActuel.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="am-empty-inline">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>
                <strong>Aucun seuil configuré</strong>
                <p>Veuillez en créer un ci-dessus</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <div className="am-stat-card am-stat-blue">
            <div className="am-stat-icon-wrapper am-stat-icon-blue">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div className="am-stat-content">
              <div className="am-stat-label">Total alertes</div>
              <div className="am-stat-value">{calculatedStats.total}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="am-stat-card am-stat-green">
            <div className="am-stat-icon-wrapper am-stat-icon-green">
              <i className="bi bi-play-circle-fill"></i>
            </div>
            <div className="am-stat-content">
              <div className="am-stat-label">Actives</div>
              <div className="am-stat-value">{calculatedStats.actives}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="am-stat-card am-stat-amber">
            <div className="am-stat-icon-wrapper am-stat-icon-amber">
              <i className="bi bi-pause-circle-fill"></i>
            </div>
            <div className="am-stat-content">
              <div className="am-stat-label">En pause</div>
              <div className="am-stat-value">{calculatedStats.enPause}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="am-stat-card am-stat-violet">
            <div className="am-stat-icon-wrapper am-stat-icon-violet">
              <i className="bi bi-lightning-fill"></i>
            </div>
            <div className="am-stat-content">
              <div className="am-stat-label">Déclenchements</div>
              <div className="am-stat-value">{calculatedStats.totalDeclenchements}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== TABLE ALERTES ========== */}
      <div className="am-table-card">
        <div className="am-table-header">
          <div className="d-flex align-items-center gap-2">
            <div className="am-table-header-icon">
              <i className="bi bi-list-ul"></i>
            </div>
            <div>
              <h6 className="am-table-title mb-0">Liste des alertes</h6>
              <small className="am-table-subtitle">
                {filteredAndSortedItems.length} alerte{filteredAndSortedItems.length > 1 ? 's' : ''}
                {selectedAlertes.length > 0 && ` • ${selectedAlertes.length} sélectionnée${selectedAlertes.length > 1 ? 's' : ''}`}
              </small>
            </div>
          </div>
        </div>

        <div className="am-table-body">
          {/* Filtres */}
          <div className="am-filters">
            <Row className="g-3 align-items-center">
              <Col lg={5} md={12}>
                <div className="am-search-wrapper">
                  <i className="bi bi-search am-search-icon"></i>
                  <input
                    type="text"
                    className="am-search-input"
                    placeholder="Rechercher une alerte..."
                    value={filterText}
                    onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
                  />
                  {filterText && (
                    <button className="am-search-clear" onClick={() => setFilterText('')}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </Col>
              <Col lg={2} md={4}>
                <select
                  className="am-select-full"
                  value={selectedTypeFilter}
                  onChange={(e) => { setSelectedTypeFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">Tous types</option>
                  <option value="Solde">Solde</option>
                  <option value="Technique">Technique</option>
                  <option value="Campagne">Campagne</option>
                  <option value="Sécurité">Sécurité</option>
                  <option value="Validation">Validation</option>
                </select>
              </Col>
              <Col lg={2} md={4}>
                <select
                  className="am-select-full"
                  value={selectedSeveriteFilter}
                  onChange={(e) => { setSelectedSeveriteFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">Toutes sévérités</option>
                  <option value="Critique">Critique</option>
                  <option value="Haute">Haute</option>
                  <option value="Moyenne">Moyenne</option>
                  <option value="Basse">Basse</option>
                </select>
              </Col>
              <Col lg={3} md={4}>
                <select
                  className="am-select-full"
                  value={selectedStatutFilter}
                  onChange={(e) => { setSelectedStatutFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">Tous statuts</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="En pause">En pause</option>
                </select>
              </Col>
            </Row>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <Table className="am-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <div className="am-checkbox-wrapper">
                      <input
                        type="checkbox"
                        className="am-checkbox"
                        checked={isAllSelected}
                        ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <SortableHeader field="titre">
                    <i className="bi bi-bell me-1"></i>Titre
                  </SortableHeader>
                  <SortableHeader field="type">
                    <i className="bi bi-tag me-1"></i>Type
                  </SortableHeader>
                  <SortableHeader field="severite">
                    <i className="bi bi-exclamation-triangle me-1"></i>Sévérité
                  </SortableHeader>
                  <SortableHeader field="statut">
                    <i className="bi bi-flag me-1"></i>Statut
                  </SortableHeader>
                  <SortableHeader field="condition">
                    <i className="bi bi-code me-1"></i>Condition
                  </SortableHeader>
                  <SortableHeader field="declenchements">
                    <i className="bi bi-lightning me-1"></i>Déclenchements
                  </SortableHeader>
                  <th className="text-end" style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((alerte) => {
                    const sevConf = getSeveriteConfig(alerte.severite);
                    const statConf = getStatutConfig(alerte.statut);
                    const typeConf = getTypeConfig(alerte.type);

                    return (
                      <tr key={alerte.id} className="am-row">
                        <td>
                          <div className="am-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="am-checkbox"
                              checked={selectedAlertes.includes(alerte.id)}
                              onChange={() => handleSelectAlerte(alerte.id)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="am-title-cell">
                            <div className="am-title-main">{alerte.titre}</div>
                            <div className="am-title-desc" title={alerte.description}>
                              {alerte.description}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="am-badge" style={{ color: typeConf.color, backgroundColor: typeConf.bg }}>
                            <i className={`bi ${typeConf.icon}`}></i>
                            {alerte.type}
                          </span>
                        </td>
                        <td>
                          <span className="am-badge" style={{ color: sevConf.color, backgroundColor: sevConf.bg }}>
                            <i className={`bi ${sevConf.icon}`}></i>
                            {alerte.severite}
                          </span>
                        </td>
                        <td>
                          <span className="am-badge" style={{ color: statConf.color, backgroundColor: statConf.bg }}>
                            <i className={`bi ${statConf.icon}`}></i>
                            {alerte.statut}
                          </span>
                        </td>
                        <td>
                          <code className="am-code">{alerte.condition}</code>
                        </td>
                        <td>
                          <span className="am-badge am-badge-violet">
                            <i className="bi bi-lightning-fill"></i>
                            {alerte.declenchements}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="am-actions">
                            <button
                              className="am-action-btn am-action-delete"
                              onClick={() => handleDeleteAlerte(alerte.id)}
                              title="Supprimer"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8">
                      <div className="am-empty-state">
                        <div className="am-empty-icon">
                          <i className="bi bi-bell-slash"></i>
                        </div>
                        <h5 className="am-empty-title">Aucune alerte trouvée</h5>
                        <p className="am-empty-text">
                          {filterText || selectedTypeFilter !== 'all' || selectedSeveriteFilter !== 'all' || selectedStatutFilter !== 'all'
                            ? "Aucune alerte ne correspond à vos critères"
                            : "Commencez par configurer un seuil d'alerte ci-dessus"}
                        </p>
                        {(filterText || selectedTypeFilter !== 'all' || selectedSeveriteFilter !== 'all' || selectedStatutFilter !== 'all') && (
                          <button
                            className="am-btn-empty-reset"
                            onClick={() => {
                              setFilterText('');
                              setSelectedTypeFilter('all');
                              setSelectedSeveriteFilter('all');
                              setSelectedStatutFilter('all');
                            }}
                          >
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            Réinitialiser les filtres
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredAndSortedItems.length > 0 && totalPages > 1 && (
            <div className="am-pagination">
              <div className="am-pagination-info">
                <i className="bi bi-info-circle"></i>
                Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
              </div>
              <div className="am-pagination-controls">
                <button className="am-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button className="am-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>

                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  const pageNum = currentPage <= 3
                    ? index + 1
                    : currentPage >= totalPages - 2
                      ? totalPages - 4 + index
                      : currentPage - 2 + index;

                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <button
                        key={pageNum}
                        className={`am-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}

                <button className="am-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button className="am-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}